import api from "../config/api";
import type { ApiResponse, PaginatedResponse } from "../types";

// Service Record Types
export interface ServiceRecord {
  _id: string;
  recordId: string;
  customer: {
    _id: string;
    name: string;
    customerId: string;
    phone: string;
    email?: string;
  };
  course: {
    _id: string;
    name: string;
    duration: number;
    category: string;
    commissionRate?: number;
  };
  booking?: {
    _id: string;
    bookingId: string;
    scheduledDate: string;
    scheduledTime: string;
  };
  therapists: Array<{
    _id: string;
    therapist: {
      _id: string;
      name: string;
      employeeId: string;
      commissionRate?: number;
    };
    startTime: string;
    endTime?: string;
    hoursWorked: number;
    isOvertime: boolean;
  }>;
  room: {
    _id: string;
    roomNumber: string;
    roomName: string;
    roomType?: 'single' | 'couple' | 'vip' | 'group';
  };
  startTime: string;
  endTime?: string;
  actualDuration?: number; // ระยะเวลาจริงในນາທີ
  status: 'in_progress' | 'completed' | 'cancelled';
  customerSatisfaction?: {
    rating: number; // 1-5
    feedback?: string;
  };
  notes?: string;
  createdBy: {
    _id: string;
    name: string;
    employeeId: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Request Interfaces
export interface CreateServiceRecordRequest {
  customer: string;
  course: string;
  booking?: string;
  therapists: Array<{
    therapist: string;
    startTime?: string;
  }>;
  room: string;
  startTime: string;
  notes?: string;
}

export interface UpdateServiceRecordRequest {
  therapists?: Array<{
    therapist: string;
    startTime: string;
    endTime?: string;
    hoursWorked?: number;
    isOvertime?: boolean;
  }>;
  notes?: string;
  customerSatisfaction?: {
    rating: number;
    feedback?: string;
  };
}

export interface CompleteServiceRequest {
  endTime?: string;
}

export interface GetServiceRecordsParams {
  page?: number;
  limit?: number;
  status?: 'in_progress' | 'completed' | 'cancelled';
  therapist?: string;
  date?: string;
  customer?: string;
  search?: string;
}

// Service Record Service
export const serviceRecordService = {
  // ดึงรายการบันทึกการให้บริการ
  getServiceRecords: async (params: GetServiceRecordsParams = {}): Promise<PaginatedResponse<ServiceRecord>> => {
    const response = await api.get('/service-records', { params });
    return response.data;
  },

  // ดึงข้อมูลบันทึกการให้บริการตาม ID
  getServiceRecord: async (id: string): Promise<ApiResponse<ServiceRecord>> => {
    const response = await api.get(`/service-records/${id}`);
    return response.data;
  },

  // สร้างบันทึกการให้บริการใหม่
  createServiceRecord: async (data: CreateServiceRecordRequest): Promise<ApiResponse<ServiceRecord>> => {
    const response = await api.post('/service-records', data);
    return response.data;
  },

  // ແກ້ໄຂบันทึกการให้บริการ
  updateServiceRecord: async (id: string, data: UpdateServiceRecordRequest): Promise<ApiResponse<ServiceRecord>> => {
    const response = await api.put(`/service-records/${id}`, data);
    return response.data;
  },

  // เสร็จสิ้นการให้บริการ
  completeService: async (id: string, data: CompleteServiceRequest = {}): Promise<ApiResponse<ServiceRecord>> => {
    const response = await api.put(`/service-records/${id}/complete`, data);
    return response.data;
  },

  // ดูการให้บริการມື້ນີ້
  getTodayServices: async (): Promise<ApiResponse<ServiceRecord[]>> => {
    const response = await api.get('/service-records/today/list');
    return response.data;
  },

  // ดูการให้บริการที่กำลังดำเนินการ
  getActiveServices: async (): Promise<ApiResponse<ServiceRecord[]>> => {
    const response = await api.get('/service-records/active/list');
    return response.data;
  },

  // Helper methods สำหรับใช้งานเฉพาะ

  // ดึงบันทึกที่เสร็จสิ้นแล้ว
  getCompletedServices: async (params: GetServiceRecordsParams = {}): Promise<PaginatedResponse<ServiceRecord>> => {
    return serviceRecordService.getServiceRecords({ ...params, status: 'completed' });
  },

  // ดึงบันทึกที่กำลังดำเนินการ
  getInProgressServices: async (params: GetServiceRecordsParams = {}): Promise<PaginatedResponse<ServiceRecord>> => {
    return serviceRecordService.getServiceRecords({ ...params, status: 'in_progress' });
  },

  // ดึงบันทึกที่ຍົກເລິກ
  getCancelledServices: async (params: GetServiceRecordsParams = {}): Promise<PaginatedResponse<ServiceRecord>> => {
    return serviceRecordService.getServiceRecords({ ...params, status: 'cancelled' });
  },

  // ดึงบันทึกตามลูกค้า
  getServicesByCustomer: async (customerId: string, params: GetServiceRecordsParams = {}): Promise<PaginatedResponse<ServiceRecord>> => {
    return serviceRecordService.getServiceRecords({ ...params, customer: customerId });
  },

  // ดึงบันทึกตามนักบำบัด
  getServicesByTherapist: async (therapistId: string, params: GetServiceRecordsParams = {}): Promise<PaginatedResponse<ServiceRecord>> => {
    return serviceRecordService.getServiceRecords({ ...params, therapist: therapistId });
  },

  // ดึงบันทึกตามวันที่
  getServicesByDate: async (date: string, params: GetServiceRecordsParams = {}): Promise<PaginatedResponse<ServiceRecord>> => {
    return serviceRecordService.getServiceRecords({ ...params, date });
  },

  // ค้นหาบันทึกการให้บริการ
  searchServiceRecords: async (search: string, params: GetServiceRecordsParams = {}): Promise<PaginatedResponse<ServiceRecord>> => {
    return serviceRecordService.getServiceRecords({ ...params, search });
  },

  // เสร็จสิ้นการให้บริการแบบง่าย (ใช้เวลาปัจจุบัน)
  completeServiceNow: async (id: string): Promise<ApiResponse<ServiceRecord>> => {
    return serviceRecordService.completeService(id, { endTime: new Date().toISOString() });
  },

  // ແກ້ໄຂความพึงพอใจลูกค้า
  updateCustomerSatisfaction: async (id: string, rating: number, feedback?: string): Promise<ApiResponse<ServiceRecord>> => {
    return serviceRecordService.updateServiceRecord(id, {
      customerSatisfaction: { rating, feedback }
    });
  },

  // เพิ่มหมายเหตุ
  addNotes: async (id: string, notes: string): Promise<ApiResponse<ServiceRecord>> => {
    return serviceRecordService.updateServiceRecord(id, { notes });
  }
};